import { useEffect, useRef, useState } from "react";
import type { ManagedTeam, TeamOperation } from "../../api/proTeams";
import { Button, Card } from "../../ui";
import { lineupIds, removablePlayers, runRosterBatch, type RosterBatch } from "./rosterBatch";

export function RosterWorkflow({ team, scope, disabled, operations, onInvite, onBusy, onChanged, onInspect }: {
  team: ManagedTeam; scope: string; disabled: boolean; onBusy: (busy: boolean) => void;
  operations: TeamOperation[]; onInvite: (steamId: string) => void;
  onChanged: () => void; onInspect: (id: string) => void;
}) {
  const storageKey = `team-roster-batch:${scope}:${team.team_id}`;
  const [batch, setBatch] = useState<RosterBatch | null>(() => {
    try { const value = JSON.parse(sessionStorage.getItem(storageKey) || "null") as RosterBatch | null;
      return value?.teamId === team.team_id && Array.isArray(value.rows) ? value : null;
    } catch { return null; }
  });
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const stop = useRef(false);
  const lock = useRef(false);
  useEffect(() => { stop.current = false; return () => { stop.current = true; }; }, []);
  const players = team.snapshot.members.filter(m => m.steam_id !== team.snapshot.bot_steam_id);
  const removable = removablePlayers(team);
  const recentInvites = [...operations].sort((a, b) => b.created_at.localeCompare(a.created_at))
    .filter(op => op.action === "invite" && op.command.steam_id)
    .filter((op, index, all) => all.findIndex(other => other.command.steam_id === op.command.steam_id) === index)
    .filter(op => !team.snapshot.members.some(m => m.steam_id === op.command.steam_id)
      && (op.invitation_pending || op.status === "failed" || op.status === "unknown"));
  const unresolved = batch?.rows.some(r => r.state === "working" || r.state === "attention");
  const remaining = batch?.rows.some(r => ["waiting", "working", "attention"].includes(r.state));
  function persist(value: RosterBatch) {
    sessionStorage.setItem(storageKey, JSON.stringify(value));
    setBatch(value);
  }
  async function run(value: RosterBatch) {
    if (lock.current) return;
    lock.current = true; stop.current = false; setRunning(true); onBusy(true); setError("");
    try { persist(value); await runRosterBatch(value, persist, () => stop.current); }
    catch { setError("Could not save progress. Keep this page open and inspect the current operation before retrying."); }
    finally { lock.current = false; setRunning(false); onBusy(false); onChanged(); }
  }
  function start(action: "invite" | "kick") {
    try {
      const ids = action === "invite" ? lineupIds(input) : removable;
      setConfirmClear(false);
      void run({ action, teamId: team.team_id, rows: ids.map(steamId => ({ steamId, key: crypto.randomUUID(), state: "waiting" })) });
    } catch (e) { setError((e as Error).message); }
  }
  return <Card title="League roster" description="Invite your lineup, check membership, then clear players when their matches are finished.">
    <div className="space-y-4">
      <ol className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-dim" aria-label="League team workflow">
        <li><strong className="text-ink">1. Prepare</strong> · invite players</li>
        <li><strong className="text-ink">2. Play</strong> · launch the match in GGLeague</li>
        <li><strong className="text-ink">3. Clear</strong> · keep the team for next time</li>
      </ol>
      <p className="text-sm"><strong>{players.length} players joined</strong> · Managing bot excluded. Dota membership does not replace GGLeague lineup validation.</p>
      <label className="block space-y-2 text-sm">
        <span>Lineup SteamID64 values</span>
        <textarea className="min-h-24 w-full rounded-md border border-line bg-surface-2 p-3 font-mono text-sm focus:border-gold-400" value={input} onChange={e => setInput(e.target.value)} placeholder="Paste one SteamID64 per player" disabled={running} />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" disabled={disabled || running || !!unresolved || !input.trim()} onClick={() => start("invite")}>Invite lineup</Button>
        <Button disabled={!players.length} onClick={async () => {
          try { await navigator.clipboard.writeText(players.map(m => m.steam_id).join("\n")); setError("Player IDs copied."); }
          catch { setError("Clipboard unavailable. Copy the IDs from the roster below."); }
        }}>Copy player IDs</Button>
        <Button variant="danger" disabled={disabled || running || !!unresolved || !removable.length} onClick={() => setConfirmClear(true)}>Clear players</Button>
      </div>
      {confirmClear && <div className="space-y-3 rounded-md border border-bad/40 p-3">
        <p>Remove these {removable.length} players from {team.snapshot.name}? Use this after their matches are finished. The managing bot and administrators stay.</p>
        <p className="break-all font-mono text-xs">{removable.join(", ")}</p>
        <Button variant="danger" disabled={disabled || running} onClick={() => start("kick")}>Remove {removable.length} {removable.length === 1 ? "player" : "players"}</Button>{" "}
        <Button onClick={() => setConfirmClear(false)}>Cancel</Button>
      </div>}
      {error && <p role="status" className="text-sm text-warn">{error}</p>}
      {recentInvites.length > 0 && <div className="border-t border-line pt-3">
        <h3 className="text-sm font-medium">Recent invitations · not in the roster</h3>
        <ul className="divide-y divide-line">{recentInvites.map(op => <li key={op.operation_id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-xs">
          <div><span className="font-mono">{op.command.steam_id}</span><p className="mt-1 text-ink-dim">{op.status === "failed" ? op.message || op.code || "Invitation failed" : "Acceptance not observed · you can resend"}</p></div>
          <div className="flex gap-2"><Button size="sm" variant="ghost" onClick={() => onInspect(op.operation_id)}>Details</Button>
            <Button size="sm" disabled={disabled || running || !!unresolved} onClick={() => onInvite(op.command.steam_id!)}>Invite again</Button></div>
        </li>)}</ul>
      </div>}
      {batch && <div className="space-y-3 border-t border-line pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p role="status" className="text-sm">{batch.action === "invite" ? "Invitations" : "Player cleanup"} · {batch.rows.filter(r => r.state === "done" || r.state === "failed").length} / {batch.rows.length} processed{running ? " · Working…" : ""}</p>
          {running ? <Button size="sm" onClick={() => { stop.current = true; }}>Pause after current request</Button>
            : remaining && <Button size="sm" disabled={disabled} onClick={() => void run(batch)}>Continue checking requests</Button>}
          {!running && !unresolved && <Button size="sm" variant="ghost" onClick={() => {
            try { sessionStorage.removeItem(storageKey); setBatch(null); } catch { setError("Could not clear saved results."); }
          }}>Dismiss results</Button>}
        </div>
        <ul className="divide-y divide-line">{batch.rows.map(row => <li key={row.key} className="flex flex-wrap items-center justify-between gap-2 py-2 text-xs">
          <div><span className="font-mono">{row.steamId}</span><p className={row.state === "failed" || row.state === "attention" ? "mt-1 text-warn" : "mt-1 text-ink-dim"}>{row.message || "Waiting to send"}</p></div>
          {row.operationId && <Button size="sm" variant="ghost" onClick={() => onInspect(row.operationId!)}>Details</Button>}
        </li>)}</ul>
        <p className="text-xs text-ink-dim">Invitations do not block other players while awaiting acceptance. Keep this page open while processing; returning here lets you continue with the same requests.</p>
      </div>}
    </div>
  </Card>;
}
